



import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';

import {
  Download,
  Filter,
  RefreshCw,
  TrendingUp,
  Store,
  Trophy,
  Calendar,
  Search,
  AlertCircle,
  Loader2,
  Gift,
  Layers,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
  Tag,
  List,
  X,
  Eye,
  PackageOpen,
} from 'lucide-react';

const emptyPagination = {
  total: 0,
  page: 1,
  limit: 50,
  totalPages: 1,
};

const emptyWinnerSummary = {
  totalWinners: 0,
  redeemedWinners: 0,
  pendingWinners: 0,
  uniqueStores: 0,
  prizeStats: [],
  subcategoryStats: [],
  storeStats: [],
  dailyStats: [],
};

const getPageItems = (current, total) => {
  const pages = [];

  if (total <= 1) {
    return [1];
  }

  const add = (page) => {
    if (
      page >= 1 &&
      page <= total &&
      !pages.includes(page)
    ) {
      pages.push(page);
    }
  };

  add(1);
  add(2);

  for (
    let page = current - 2;
    page <= current + 2;
    page += 1
  ) {
    add(page);
  }

  add(total - 1);
  add(total);

  const sorted = pages.sort(
    (a, b) => a - b
  );

  const result = [];

  for (
    let index = 0;
    index < sorted.length;
    index += 1
  ) {
    if (
      index > 0 &&
      sorted[index] -
        sorted[index - 1] >
        1
    ) {
      result.push('...');
    }

    result.push(sorted[index]);
  }

  return result;
};

const normalizeSubcategoryStats = (
  value
) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (
          typeof item === 'string'
        ) {
          return {
            name: item,
            count: 1,
          };
        }

        const name =
          item.subcategory ||
          item.subCategory ||
          item.wonSubcategory ||
          item.won_subcategory ||
          item.category ||
          item.name ||
          item.label ||
          '';

        const count =
          Number(
            item.count ??
              item.wins ??
              item.totalWins ??
              item.total_wins ??
              item.winnerCount ??
              item.quantity ??
              0
          );

        return {
          name: String(
            name || ''
          ).trim(),

          count:
            Number.isFinite(count)
              ? count
              : 0,
        };
      })
      .filter(
        (item) => item.name
      );
  }

  if (
    typeof value === 'object'
  ) {
    return Object.entries(
      value
    )
      .map(
        ([name, count]) => ({
          name: String(name),
          count:
            Number(count) || 0,
        })
      )
      .filter(
        (item) => item.name
      );
  }

  return [];
};

const getStoreSubcategories = (
  store
) => {
  const raw =
    store?.wonSubcategories ??
    store?.won_subcategories ??
    store?.subcategoriesWon ??
    store?.winnerSubcategories ??
    store?.winner_subcategories ??
    store?.subcategories;

  return normalizeSubcategoryStats(
    raw
  ).sort(
    (a, b) =>
      Number(b.count || 0) -
      Number(a.count || 0)
  );
};

const Reports = () => {
  const defaultFilters = {
    region: 'all',
    city: '',
    startDate: '',
    endDate: '',
    prize: '',
    category: 'all',
  };

  const [
    filters,
    setFilters,
  ] = useState(
    defaultFilters
  );

  const [
    appliedFilters,
    setAppliedFilters,
  ] = useState(
    defaultFilters
  );

  const [
    cities,
    setCities,
  ] = useState([]);

  const [
    prizes,
    setPrizes,
  ] = useState([]);

  const [
    stores,
    setStores,
  ] = useState([]);

  const [
    winners,
    setWinners,
  ] = useState([]);

  const [
    categoryStats,
    setCategoryStats,
  ] = useState([]);

  const [
    discountSummary,
    setDiscountSummary,
  ] = useState([]);

  const [
    discountList,
    setDiscountList,
  ] = useState([]);

  const [
    winnerSummary,
    setWinnerSummary,
  ] = useState(
    emptyWinnerSummary
  );

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    winnersLoading,
    setWinnersLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState(null);

  const [
    winnersError,
    setWinnersError,
  ] = useState(null);

  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');

  const [
    winnersSearch,
    setWinnersSearch,
  ] = useState('');

  const [
    appliedWinnersSearch,
    setAppliedWinnersSearch,
  ] = useState('');

  const [
    categorySearch,
    setCategorySearch,
  ] = useState('');

  const [
    codesSearch,
    setCodesSearch,
  ] = useState('');

  const [
    winnersPage,
    setWinnersPage,
  ] = useState(1);

  const winnersLimit = 50;

  const [
    winnersPagination,
    setWinnersPagination,
  ] = useState(
    emptyPagination
  );

  const [
    selectedStore,
    setSelectedStore,
  ] = useState(null);

  const [
    storeWinnerModalOpen,
    setStoreWinnerModalOpen,
  ] = useState(false);

  // ============================================================
  // WINNER ROW MAPPING
  // ============================================================

  const mapWinnerRow =
    useCallback(
      (winner) => ({
        id:
          winner.id,

        customerName:
          winner.customerName ||
          winner.playerName ||
          winner.customer ||
          winner.name ||
          '-',

        phone:
          winner.phone ||
          winner.mobileNumber ||
          winner.phoneNumber ||
          winner.mobile ||
          '-',

        storeName:
          winner.storeName ||
          winner.store?.storeName ||
          '-',

        city:
          winner.city ||
          winner.store?.city ||
          '-',

        region:
          winner.region ||
          winner.store?.region ||
          '-',

        prize:
          winner.wonValue ||
          winner.prize ||
          winner.prizeWon ||
          '-',

        code:
          winner.discountCode ||
          winner.code ||
          winner.couponCode ||
          '-',

        subcategory:
          winner.wonSubcategory ||
          winner.subcategory ||
          winner.subCategory ||
          '',

        status:
          winner.status ||
          winner.spinStatus ||
          'N/A',

        storeId:
          winner.storeId ??
          winner.store_id ??
          winner.store?.id ??
          '-',

        isRedeemed:
          typeof winner.isRedeemed ===
          'boolean'
            ? winner.isRedeemed
              ? 'Yes'
              : 'No'
            : winner.isRedeemed ===
                  1 ||
              winner.isRedeemed ===
                '1' ||
              String(
                winner.isRedeemed
              ).toLowerCase() ===
                'yes' ||
              String(
                winner.isRedeemed
              ).toLowerCase() ===
                'true'
            ? 'Yes'
            : 'No',


            gender:
  winner.gender ||
  winner.customerGender ||
  winner.userGender ||
  '-',
        createdAt:
          winner.createdAt ||
          winner.spinTime,

        updatedAt:
          winner.updatedAt,
      }),
      []
    );

  // ============================================================
  // DROPDOWN LISTS
  // ============================================================

  const fetchLists =
    useCallback(async () => {
      try {
        const [
          citiesRes,
          prizesRes,
        ] = await Promise.all([
          api.get(
            '/api/reports/cities'
          ),

          api.get(
            '/api/reports/prizes'
          ),
        ]);

        setCities(
          Array.isArray(
            citiesRes.data
          )
            ? citiesRes.data
            : []
        );

        setPrizes(
          Array.isArray(
            prizesRes.data
          )
            ? prizesRes.data
            : []
        );
      } catch (err) {
        console.error(
          'Dropdown lists error:',
          err
        );

        setCities([]);
        setPrizes([]);
      }
    }, []);

  // ============================================================
  // OTHER REPORT DATA
  // ============================================================

  const fetchReportData =
    useCallback(async () => {
      setLoading(true);
      setError(null);

      try {
const [
  storeRes,
  categoryRes,
  discountRes,
  winnerSummaryRes,
] = await Promise.all([
  api
    .post(
      '/api/reports/store-data',
      appliedFilters
    )
    .catch((err) => {
      console.error(
        'Store Data API Error:',
        err
      );

      return {
        data: {
          stores: [],
        },
      };
    }),

  api
    .get(
      '/api/reports/category-stats'
    )
    .catch((err) => {
      console.error(
        'Category Stats API Error:',
        err
      );

      return {
        data: {
          data: [],
        },
      };
    }),

  api
    .get(
      '/api/reports/discount-stats'
    )
    .catch((err) => {
      console.error(
        'Discount Stats API Error:',
        err
      );

      return {
        data: {
          data: {
            summary: [],
            list: [],
          },
        },
      };
    }),

  api
    .get(
      '/api/reports/winner-summary',
      {
        params: {
          prize:
            appliedFilters.prize || '',

          region:
            appliedFilters.region || '',

          city:
            appliedFilters.city || '',

          startDate:
            appliedFilters.startDate || '',

          endDate:
            appliedFilters.endDate || '',
        },
      }
    )
    .catch((err) => {
      console.error(
        'Winner Summary API Error:',
        err
      );

      return {
        data: {
          data:
            emptyWinnerSummary,
        },
      };
    }),
]);
        // ======================================================
        // STORE DATA
        // ======================================================

        const storesData =
          Array.isArray(
            storeRes.data?.stores
          )
            ? storeRes.data.stores
            : [];

        const normalizedStores =
          storesData.map(
            (store) => ({
              ...store,

              spins_used:
                Number(
                  store.spins_used || 0
                ),

              winners_count:
                Number(
                  store.winners_count ||
                    0
                ),

              spins_remaining:
                Number(
                  store.spins_remaining ||
                    0
                ),

              spinLimit:
                Number(
                  store.spinLimit || 0
                ),

              wonSubcategories:
                getStoreSubcategories(
                  store
                ),
            })
          );

        setStores(
          normalizedStores
        );

        // ======================================================
        // CATEGORY DATA
        // ======================================================

        const categoryData =
          Array.isArray(
            categoryRes.data?.data
          )
            ? categoryRes.data.data
            : [];

        setCategoryStats(
          categoryData
        );

        // ======================================================
        // DISCOUNT DATA
        // ======================================================

        const discountData =
          discountRes.data?.data ||
          {};

        setDiscountSummary(
          Array.isArray(
            discountData.summary
          )
            ? discountData.summary
            : []
        );

        setDiscountList(
          Array.isArray(
            discountData.list
          )
            ? discountData.list
            : []
        );

        // ======================================================
        // WINNER SUMMARY
        // ======================================================

        const summary =
          winnerSummaryRes.data
            ?.data || {};

        setWinnerSummary({
          totalWinners:
            Number(
              summary.totalWinners ||
                0
            ),

          redeemedWinners:
            Number(
              summary.redeemedWinners ||
                0
            ),

          pendingWinners:
            Number(
              summary.pendingWinners ||
                0
            ),

          uniqueStores:
            Number(
              summary.uniqueStores ||
                0
            ),

          prizeStats:
            Array.isArray(
              summary.prizeStats
            )
              ? summary.prizeStats
              : [],

          subcategoryStats:
            Array.isArray(
              summary.subcategoryStats
            )
              ? summary.subcategoryStats
              : [],

          storeStats:
            Array.isArray(
              summary.storeStats
            )
              ? summary.storeStats
              : [],

          dailyStats:
            Array.isArray(
              summary.dailyStats
            )
              ? summary.dailyStats
              : [],
        });
      } catch (err) {
        console.error(
          'Report fetch error:',
          err
        );

        console.error(
          'Report error response:',
          err.response?.data
        );

        setError(
          err.response?.data?.error ||
            err.message ||
            'Failed to load report data.'
        );

        setStores([]);
        setCategoryStats([]);
        setDiscountSummary([]);
        setDiscountList([]);

        setWinnerSummary(
          emptyWinnerSummary
        );
      } finally {
        setLoading(false);
      }
    }, [appliedFilters]);

  // ============================================================
  // WINNERS DETAIL
  // ============================================================

  const fetchWinners =
  useCallback(async () => {
    setWinnersLoading(true);
    setWinnersError(null);

    try {
      const params = {
        page: winnersPage,
        limit: winnersLimit,

        search:
          appliedWinnersSearch || '',

        prize:
          appliedFilters.prize || '',

        startDate:
          appliedFilters.startDate || '',

        endDate:
          appliedFilters.endDate || '',

        region:
          appliedFilters.region || '',

        city:
          appliedFilters.city || '',

        debug:
          '1',
      };

      console.log(
        '🏆 WINNERS REQUEST:',
        params
      );

     const response =
  await api.get(
    '/api/reports/winner-detail',
    {
      params,
    }
  );

      console.log(
        '🏆 WINNERS RESPONSE:',
        response.data
      );

      const responseData =
        response.data || {};

      const winnersRaw =
        Array.isArray(
          responseData.winners
        )
          ? responseData.winners
          : Array.isArray(
              responseData.data
            )
          ? responseData.data
          : [];

      console.log(
        '📦 Winners received:',
        winnersRaw.length
      );

      console.table(
        winnersRaw.map(
          (winner) => ({
            id:
              winner.id,

            name:
              winner.customerName ||
              winner.playerName,

            phone:
              winner.phone ||
              winner.mobileNumber,

            code:
              winner.code ||
              winner.discountCode,

            amount:
              winner.prize ||
              winner.wonValue,

            redeemed:
              winner.isRedeemed,

            status:
              winner.status,

            spinTime:
              winner.createdAt,
          })
        )
      );

      const winnersData =
        winnersRaw.map(
          mapWinnerRow
        );

      console.log(
        '🎯 Mapped winners:',
        winnersData.length
      );

      setWinners(
        winnersData
      );

      const pagination =
        responseData.pagination ||
        {};

      setWinnersPagination({
        total:
          Number(
            pagination.total ??
              responseData.total ??
              0
          ),

        page:
          Number(
            pagination.page ??
              winnersPage
          ),

        limit:
          Number(
            pagination.limit ??
              winnersLimit
          ),

        totalPages:
          Number(
            pagination.totalPages ??
              1
          ),
      });
    } catch (err) {
      console.error(
        '❌ Winner API ERROR:',
        err
      );

      console.error(
        '❌ Status:',
        err.response?.status
      );

      console.error(
        '❌ Backend response:',
        err.response?.data
      );

      setWinners([]);

      setWinnersPagination(
        emptyPagination
      );

      setWinnersError(
        err.response?.data?.error ||
          err.message ||
          'Winner detail API failed.'
      );
    } finally {
      setWinnersLoading(false);
    }
  }, [
    appliedFilters,
    appliedWinnersSearch,
    winnersPage,
    winnersLimit,
    mapWinnerRow,
  ]);

  // ============================================================
  // EFFECTS
  // ============================================================

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  useEffect(() => {
    fetchWinners();
  }, [fetchWinners]);

  useEffect(() => {
    const timer =
      setTimeout(() => {
        setAppliedWinnersSearch(
          winnersSearch.trim()
        );

        setWinnersPage(1);
      }, 350);

    return () =>
      clearTimeout(timer);
  }, [winnersSearch]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const openStoreWinnerModal = (
    store
  ) => {
    setSelectedStore(store);
    setStoreWinnerModalOpen(
      true
    );
  };

  const closeStoreWinnerModal =
    () => {
      setStoreWinnerModalOpen(
        false
      );

      setSelectedStore(null);
    };

  const handleFilterChange =
    (event) => {
      const {
        name,
        value,
      } = event.target;

      setFilters(
        (previous) => ({
          ...previous,
          [name]: value,
        })
      );
    };

  const applyFilters = () => {
    setAppliedFilters(
      filters
    );

    setWinnersPage(1);
  };

  const resetFilters = () => {
    setFilters(
      defaultFilters
    );

    setAppliedFilters(
      defaultFilters
    );

    setWinnersPage(1);

    setSearchTerm('');

    setWinnersSearch('');

    setAppliedWinnersSearch('');

    setCategorySearch('');

    setCodesSearch('');

    setWinnerSummary(
      emptyWinnerSummary
    );
  };

  // ============================================================
  // STORE CSV
  // ============================================================

  const exportCSV = () => {
    const headers = [
      'Store Name',
      'City',
      'Region',
      'Win Rate %',
      'Winners',
      'Won Subcategories',
      'Last Activity',
    ];

    const rows = stores.map(
      (store) => {
        const winRate =
          store.spins_used > 0
            ? Math.round(
                ((store.winners_count ||
                  0) /
                  store.spins_used) *
                  100
              )
            : 0;

        const subcategories =
          getStoreSubcategories(
            store
          )
            .map(
              (item) =>
                `${item.name} (${item.count})`
            )
            .join(' | ');

        return [
          `"${String(
            store.storeName || ''
          ).replace(/"/g, '""')}"`,

          `"${String(
            store.city || '-'
          ).replace(/"/g, '""')}"`,

          `"${String(
            store.region || '-'
          ).replace(/"/g, '""')}"`,

          winRate,

          store.winners_count ||
            0,

          `"${subcategories.replace(
            /"/g,
            '""'
          )}"`,

          store.last_activity
            ? new Date(
                store.last_activity
              ).toLocaleString()
            : '-',
        ];
      }
    );

    const csv = [
      headers,
      ...rows,
    ]
      .map(
        (row) =>
          row.join(',')
      )
      .join('\n');

    const blob = new Blob(
      [csv],
      {
        type:
          'text/csv;charset=utf-8;',
      }
    );

    const url =
      URL.createObjectURL(
        blob
      );

    const link =
      document.createElement(
        'a'
      );

    link.href = url;

    link.download =
      `store_report_${new Date()
        .toISOString()
        .slice(0, 19)}.csv`;

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    URL.revokeObjectURL(url);
  };

  // ============================================================
  // WINNERS CSV
  // ============================================================

  const exportWinnersCSV =
    async () => {
      try {
        const response =
          await api.get(
            '/api/spin/latest-winners',
            {
              params: {
                prize:
                  appliedFilters.prize ||
                  '',

                region:
                  appliedFilters.region ||
                  '',

                city:
                  appliedFilters.city ||
                  '',

                startDate:
                  appliedFilters.startDate ||
                  '',

                endDate:
                  appliedFilters.endDate ||
                  '',

                search:
                  appliedWinnersSearch ||
                  '',

                page: 1,

                limit: 'all',
              },
            }
          );

        const rawWinners =
          Array.isArray(
            response.data?.winners
          )
            ? response.data.winners
            : [];

        const allWinners =
          rawWinners.map(
            mapWinnerRow
          );

        const headers = [
          '#',
          'Customer Name',
          'Phone',
          'Store',
          'City',
          'Region',
          'Coupon Code',
          'Discount Amount',
          'Subcategory',
          'Redeemed',
          'Spin Time',
          'Status',
        ];

        const rows =
          allWinners.map(
            (
              winner,
              index
            ) => [
              index + 1,

              `"${String(
                winner.customerName ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,

              `"${String(
                winner.phone ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,

              `"${String(
                winner.storeName ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,

              `"${String(
                winner.city ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,

              `"${String(
                winner.region ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,

              `"${String(
                winner.code ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,

              `"${String(
                winner.prize ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,

              `"${String(
                winner.subcategory ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,

              `"${String(
                winner.isRedeemed ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,

              winner.createdAt
                ? new Date(
                    winner.createdAt
                  ).toLocaleString()
                : '-',

              `"${String(
                winner.status ||
                  '-'
              ).replace(
                /"/g,
                '""'
              )}"`,
            ]
          );

        const csv = [
          headers,
          ...rows,
        ]
          .map(
            (row) =>
              row.join(',')
          )
          .join('\n');

        const blob = new Blob(
          [csv],
          {
            type:
              'text/csv;charset=utf-8;',
          }
        );

        const url =
          URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            'a'
          );

        link.href = url;

        link.download =
          `winners_report_${new Date()
            .toISOString()
            .slice(0, 19)}.csv`;

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );

        URL.revokeObjectURL(
          url
        );
      } catch (err) {
        console.error(
          'Export winners CSV failed:',
          err
        );

        setWinnersError(
          'Failed to export winners CSV.'
        );
      }
    };

  // ============================================================
  // FILTERED DATA
  // ============================================================

  const filteredStores =
    stores.filter(
      (store) => {
        const search =
          searchTerm.toLowerCase();

        return (
          String(
            store.storeName || ''
          )
            .toLowerCase()
            .includes(search) ||
          String(
            store.city || ''
          )
            .toLowerCase()
            .includes(search) ||
          String(
            store.region || ''
          )
            .toLowerCase()
            .includes(search)
        );
      }
    );

  const filteredCategories =
    categoryStats.filter(
      (category) => {
        const search =
          categorySearch.toLowerCase();

        return (
          String(
            category.subcategory ||
              ''
          )
            .toLowerCase()
            .includes(search) ||
          String(
            category.label ||
              category.lineItemName ||
              category.name ||
              ''
          )
            .toLowerCase()
            .includes(search) ||
          String(
            category.winWeight ||
              category.winweight ||
              ''
          )
            .toLowerCase()
            .includes(search) ||
          String(
            category.status ||
              (category.isActive ===
              true
                ? 'active'
                : 'inactive')
          )
            .toLowerCase()
            .includes(search) ||
          String(
            category.gender || ''
          )
            .toLowerCase()
            .includes(search)
        );
      }
    );

  const filteredDiscountList =
    discountList.filter(
      (item) => {
        const search =
          codesSearch.toLowerCase();

        return (
          String(
            item.code || ''
          )
            .toLowerCase()
            .includes(search) ||
          String(
            item.percentage || ''
          ).includes(
            codesSearch
          )
        );
      }
    );

  const getDiscountSummaryDetails =
    (stat) => {
      const category =
        stat?.category ||
        stat?.categoryName ||
        stat?.subcategory ||
        stat?.subCategory ||
        'Uncategorized';

      let addedTime = '-';

      const value =
        stat?.addedTime ||
        stat?.createdAt ||
        stat?.addedAt ||
        stat?.generatedAt ||
        stat?.created_at ||
        stat?.generated_at ||
        '';

      if (value) {
        const date =
          new Date(value);

        if (
          !Number.isNaN(
            date.getTime()
          )
        ) {
          addedTime =
            date.toLocaleString();
        }
      }

      return {
        category:
          String(
            category
          ).trim(),

        addedTime,
      };
    };

  const goToPage = (
    page
  ) => {
    const nextPage =
      Math.max(
        1,
        Math.min(
          page,
          winnersPagination.totalPages ||
            1
        )
      );

    setWinnersPage(
      nextPage
    );
  };

  const pageItems =
    getPageItems(
      winnersPagination.page,
      winnersPagination.totalPages
    );

  const selectedStoreSubcategories =
    selectedStore
      ? getStoreSubcategories(
          selectedStore
        )
      : [];

  const selectedStoreWinners =
    selectedStore
      ? Number(
          selectedStore.winners_count ||
            0
        )
      : 0;

  // ============================================================
  // UI
  // ============================================================

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">

          {/* HEADER */}

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">

            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
                <TrendingUp className="text-indigo-600" />
                Campaign Reports
              </h1>

              <p className="text-sm text-gray-500 mt-1">
                Detailed store, discount-code, category and winner reports
              </p>
            </div>

            <div className="flex gap-3 flex-wrap">

              <button
                onClick={() => {
                  fetchReportData();
                  fetchWinners();
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-md transition"
              >
                <RefreshCw size={16} />
                Refresh
              </button>

              <button
                onClick={exportCSV}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-md transition"
              >
                <Download size={16} />
                Export Stores CSV
              </button>

              <button
                onClick={
                  exportWinnersCSV
                }
                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-md transition"
              >
                <Trophy size={16} />
                Export Winners CSV
              </button>

            </div>
          </div>

          {/* FILTERS */}

          <div className="bg-white rounded-2xl shadow-lg p-5 mb-6 border border-gray-100">

            <div className="flex flex-wrap gap-4 items-end">

              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  <Search
                    size={12}
                    className="inline mr-1"
                  />
                  City
                </label>

                <select
                  name="city"
                  value={
                    filters.city
                  }
                  onChange={
                    handleFilterChange
                  }
                  className="w-full border rounded-xl p-2.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">
                    All Cities
                  </option>

                  {cities.map(
                    (city) => (
                      <option
                        key={city}
                        value={city}
                      >
                        {city}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  <Gift
                    size={12}
                    className="inline mr-1"
                  />
                  Prize
                </label>

                <select
                  name="prize"
                  value={
                    filters.prize
                  }
                  onChange={
                    handleFilterChange
                  }
                  className="w-full border rounded-xl p-2.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">
                    All Prizes
                  </option>

                  {prizes.map(
                    (prize) => (
                      <option
                        key={prize}
                        value={prize}
                      >
                        {prize}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  <Calendar
                    size={12}
                    className="inline mr-1"
                  />
                  From Date
                </label>

                <input
                  type="date"
                  name="startDate"
                  value={
                    filters.startDate
                  }
                  onChange={
                    handleFilterChange
                  }
                  className="w-full border rounded-xl p-2.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  <Calendar
                    size={12}
                    className="inline mr-1"
                  />
                  To Date
                </label>

                <input
                  type="date"
                  name="endDate"
                  value={
                    filters.endDate
                  }
                  onChange={
                    handleFilterChange
                  }
                  className="w-full border rounded-xl p-2.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              <button
                onClick={
                  applyFilters
                }
                className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl flex items-center gap-2 hover:bg-indigo-700 transition"
              >
                <Filter size={18} />
                Apply
              </button>

              <button
                onClick={
                  resetFilters
                }
                className="border border-gray-300 text-gray-600 px-5 py-2.5 rounded-xl hover:bg-gray-100 transition"
              >
                Reset
              </button>

            </div>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-lg flex items-center gap-2">
              <AlertCircle size={20} />
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2
                className="animate-spin text-indigo-600"
                size={40}
              />
            </div>
          ) : (
            <>

              {/* WINNER SUMMARY */}

              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-6">

                <div className="px-6 py-4 border-b border-gray-100 bg-indigo-900 text-white">
                  <h2 className="font-bold flex items-center gap-2 text-lg">
                    <Trophy
                      size={20}
                      className="text-indigo-200"
                    />
                    Winner Summary
                  </h2>

                  <p className="text-xs text-indigo-200 mt-1">
                    Complete winner statistics based on selected filters
                  </p>
                </div>

                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                  <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-5">
                    <p className="text-sm text-gray-500">
                      Total Winners
                    </p>

                    <p className="text-3xl font-bold text-indigo-700 mt-2">
                      {
                        winnerSummary.totalWinners
                      }
                    </p>
                  </div>

                  <div className="rounded-xl border border-green-100 bg-green-50 p-5">
                    <p className="text-sm text-gray-500">
                      Redeemed Winners
                    </p>

                    <p className="text-3xl font-bold text-green-700 mt-2">
                      {
                        winnerSummary.redeemedWinners
                      }
                    </p>
                  </div>

                  <div className="rounded-xl border border-amber-100 bg-amber-50 p-5">
                    <p className="text-sm text-gray-500">
                      Pending Winners
                    </p>

                    <p className="text-3xl font-bold text-amber-700 mt-2">
                      {
                        winnerSummary.pendingWinners
                      }
                    </p>
                  </div>

                  <div className="rounded-xl border border-purple-100 bg-purple-50 p-5">
                    <p className="text-sm text-gray-500">
                      Winning Stores
                    </p>

                    <p className="text-3xl font-bold text-purple-700 mt-2">
                      {
                        winnerSummary.uniqueStores
                      }
                    </p>
                  </div>

                </div>

                <div className="px-5 pb-5 grid grid-cols-1 xl:grid-cols-3 gap-5">

                  <div className="border border-gray-200 rounded-xl overflow-hidden">

                    <div className="px-4 py-3 bg-slate-50 border-b border-gray-200">
                      <h3 className="font-bold text-gray-700 flex items-center gap-2">
                        <Gift
                          size={17}
                          className="text-indigo-600"
                        />
                        Prize-wise Winners
                      </h3>
                    </div>

                    <div className="max-h-[320px] overflow-y-auto">

                      {winnerSummary.prizeStats.length === 0 ? (
                        <div className="py-10 text-center text-gray-400 text-sm">
                          No prize data available
                        </div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-white sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left">
                                Prize
                              </th>

                              <th className="px-3 py-3 text-center">
                                Total
                              </th>

                              <th className="px-3 py-3 text-center">
                                Redeem
                              </th>

                              <th className="px-3 py-3 text-center">
                                Pending
                              </th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-100">

                            {winnerSummary.prizeStats.map(
                              (
                                item,
                                index
                              ) => (
                                <tr
                                  key={`${item.prize}-${index}`}
                                  className="hover:bg-slate-50"
                                >
                                  <td className="px-4 py-3 font-semibold text-gray-700">
                                    {
                                      item.prize
                                    }
                                  </td>

                                  <td className="px-3 py-3 text-center font-bold text-indigo-700">
                                    {
                                      item.total
                                    }
                                  </td>

                                  <td className="px-3 py-3 text-center font-bold text-green-600">
                                    {
                                      item.redeemed
                                    }
                                  </td>

                                  <td className="px-3 py-3 text-center font-bold text-amber-600">
                                    {
                                      item.pending
                                    }
                                  </td>
                                </tr>
                              )
                            )}

                          </tbody>
                        </table>
                      )}

                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden">

                    <div className="px-4 py-3 bg-slate-50 border-b border-gray-200">
                      <h3 className="font-bold text-gray-700 flex items-center gap-2">
                        <Layers
                          size={17}
                          className="text-purple-600"
                        />
                        Subcategory-wise Winners
                      </h3>
                    </div>

                    <div className="max-h-[320px] overflow-y-auto">

                      {winnerSummary.subcategoryStats.length === 0 ? (
                        <div className="py-10 text-center text-gray-400 text-sm">
                          No subcategory data available
                        </div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-white sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left">
                                Subcategory
                              </th>

                              <th className="px-3 py-3 text-center">
                                Total
                              </th>

                              <th className="px-3 py-3 text-center">
                                Redeem
                              </th>

                              <th className="px-3 py-3 text-center">
                                Pending
                              </th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-100">

                            {winnerSummary.subcategoryStats.map(
                              (
                                item,
                                index
                              ) => (
                                <tr
                                  key={`${item.subcategory}-${index}`}
                                  className="hover:bg-slate-50"
                                >
                                  <td className="px-4 py-3 font-semibold text-gray-700">
                                    {
                                      item.subcategory
                                    }
                                  </td>

                                  <td className="px-3 py-3 text-center font-bold text-indigo-700">
                                    {
                                      item.total
                                    }
                                  </td>

                                  <td className="px-3 py-3 text-center font-bold text-green-600">
                                    {
                                      item.redeemed
                                    }
                                  </td>

                                  <td className="px-3 py-3 text-center font-bold text-amber-600">
                                    {
                                      item.pending
                                    }
                                  </td>
                                </tr>
                              )
                            )}

                          </tbody>
                        </table>
                      )}

                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden">

                    <div className="px-4 py-3 bg-slate-50 border-b border-gray-200">
                      <h3 className="font-bold text-gray-700 flex items-center gap-2">
                        <Store
                          size={17}
                          className="text-teal-600"
                        />
                        Store-wise Winners
                      </h3>
                    </div>

                    <div className="max-h-[320px] overflow-y-auto">

                      {winnerSummary.storeStats.length === 0 ? (
                        <div className="py-10 text-center text-gray-400 text-sm">
                          No store winner data available
                        </div>
                      ) : (
                        <table className="w-full text-sm">

                          <thead className="bg-white sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left">
                                Store
                              </th>

                              <th className="px-3 py-3 text-center">
                                Total
                              </th>

                              <th className="px-3 py-3 text-center">
                                Redeem
                              </th>

                              <th className="px-3 py-3 text-center">
                                Pending
                              </th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-100">

                            {winnerSummary.storeStats.map(
                              (
                                item,
                                index
                              ) => (
                                <tr
                                  key={`${item.storeId}-${index}`}
                                  className="hover:bg-slate-50"
                                >
                                  <td className="px-4 py-3">
                                    <p className="font-semibold text-gray-700">
                                      {
                                        item.storeName
                                      }
                                    </p>

                                    <p className="text-xs text-gray-400">
                                      {
                                        item.city
                                      }

                                      {item.region
                                        ? ` • ${item.region}`
                                        : ''}
                                    </p>
                                  </td>

                                  <td className="px-3 py-3 text-center font-bold text-indigo-700">
                                    {
                                      item.total
                                    }
                                  </td>

                                  <td className="px-3 py-3 text-center font-bold text-green-600">
                                    {
                                      item.redeemed
                                    }
                                  </td>

                                  <td className="px-3 py-3 text-center font-bold text-amber-600">
                                    {
                                      item.pending
                                    }
                                  </td>
                                </tr>
                              )
                            )}

                          </tbody>
                        </table>
                      )}

                    </div>
                  </div>

                </div>
              </div>

              {/* DISCOUNT CODES */}

              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-6">

                <div className="px-6 py-4 border-b border-gray-100 bg-indigo-900 text-white">
                  <h2 className="font-bold flex items-center gap-2 text-lg">
                    <Tag
                      size={20}
                      className="text-indigo-200"
                    />
                    Discount Codes Summary
                  </h2>
                </div>

                <div className="overflow-x-auto">

                  <table className="w-full text-sm">

                    <thead className="bg-slate-100 text-gray-700 text-xs font-bold uppercase tracking-wider">

                      <tr>

                        <th className="px-5 py-3.5 text-left">
                          Discount Percentage
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          Category
                        </th>

                        <th className="px-5 py-3.5 text-center">
                          Total Generated
                        </th>

                        <th className="px-5 py-3.5 text-center">
                          Used Codes
                        </th>

                        <th className="px-5 py-3.5 text-center">
                          Available To Use
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          Usage Progress
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          Added Time
                        </th>

                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">

                      {discountSummary.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center py-10 text-gray-400"
                          >
                            No discount statistics found.
                          </td>
                        </tr>
                      ) : (
                        discountSummary.map(
                          (
                            stat,
                            index
                          ) => {
                            const total =
                              parseInt(
                                stat.total_codes ||
                                  0,
                                10
                              );

                            const used =
                              parseInt(
                                stat.used_codes ||
                                  0,
                                10
                              );

                            const available =
                              Math.max(
                                total -
                                  used,
                                0
                              );

                            const usagePct =
                              total > 0
                                ? Math.round(
                                    (used /
                                      total) *
                                      100
                                  )
                                : 0;

                            const {
                              category,
                              addedTime,
                            } =
                              getDiscountSummaryDetails(
                                stat
                              );

                            return (
                              <tr
                                key={index}
                                className="hover:bg-slate-50 transition"
                              >

                                <td className="px-5 py-3 font-bold text-slate-800">
                                  <span className="bg-purple-100 text-purple-800 px-2.5 py-1 rounded-md">
                                    {
                                      stat.percentage
                                    }
                                    PKR
                                  </span>
                                </td>

                                <td className="px-5 py-3">
                                  <span className="bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-md text-xs font-semibold">
                                    {
                                      category
                                    }
                                  </span>
                                </td>

                                <td className="px-5 py-3 text-center">
                                  {total}
                                </td>

                                <td className="px-5 py-3 text-center font-bold text-green-600">
                                  {used}
                                </td>

                                <td className="px-5 py-3 text-center font-semibold text-blue-600">
                                  {available}
                                </td>

                                <td className="px-5 py-3">
                                  <div className="flex items-center gap-2">

                                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full ${
                                          usagePct >=
                                          90
                                            ? 'bg-red-600'
                                            : usagePct >=
                                              70
                                            ? 'bg-orange-500'
                                            : 'bg-indigo-500'
                                        }`}
                                        style={{
                                          width: `${usagePct}%`,
                                        }}
                                      />
                                    </div>

                                    <span className="text-[11px] text-gray-500">
                                      {usagePct}%
                                    </span>

                                  </div>
                                </td>

                                <td className="px-5 py-3 text-gray-500 text-xs whitespace-nowrap">
                                  {addedTime}
                                </td>

                              </tr>
                            );
                          }
                        )
                      )}

                    </tbody>
                  </table>
                </div>
              </div>

              {/* INDIVIDUAL CODES */}

              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-6">

                <div className="px-6 py-4 border-b border-gray-100 bg-indigo-900 text-white flex flex-wrap justify-between items-center gap-3">

                  <h2 className="font-bold flex items-center gap-2 text-lg">
                    <List
                      size={20}
                      className="text-indigo-200"
                    />
                    All Individual Generated Codes
                  </h2>

                  <div className="relative w-64 text-gray-800">

                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={16}
                    />

                    <input
                      type="text"
                      placeholder="Search code or percentage..."
                      className="pl-9 pr-4 py-1.5 border rounded-lg w-full text-sm"
                      value={
                        codesSearch
                      }
                      onChange={(event) =>
                        setCodesSearch(
                          event.target.value
                        )
                      }
                    />

                  </div>

                </div>

                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">

                  <table className="w-full text-sm">

                    <thead className="bg-slate-100 text-gray-700 text-xs font-bold uppercase tracking-wider sticky top-0 z-10">

                      <tr>

                        <th className="px-5 py-3.5 text-left">
                          Discount Code
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          AMOUNT
                        </th>

                        <th className="px-5 py-3.5 text-center">
                          Status
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          Generated On
                        </th>

                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">

                      {filteredDiscountList.length === 0 ? (
                        <tr>
                          <td
                            colSpan="4"
                            className="text-center py-10 text-gray-400"
                          >
                            No discount codes match your search.
                          </td>
                        </tr>
                      ) : (
                        filteredDiscountList.map(
                          (
                            item,
                            index
                          ) => {
                            const isUsed =
                              item.isUsed ===
                                1 ||
                              String(
                                item.isUsed
                              ).toLowerCase() ===
                                'true' ||
                              String(
                                item.isUsed
                              ) ===
                                '1';

                            const isExpired =
                              item.expireCode ===
                                1 ||
                              String(
                                item.expireCode
                              ).toLowerCase() ===
                                'true' ||
                              String(
                                item.expireCode
                              ) ===
                                '1';

                            let statusText =
                              'Available';

                            let statusClasses =
                              'bg-blue-100 text-blue-700';

                            if (isUsed) {
                              statusText =
                                'Used';

                              statusClasses =
                                'bg-green-100 text-green-700';
                            } else if (
                              isExpired
                            ) {
                              statusText =
                                'Expired';

                              statusClasses =
                                'bg-red-100 text-red-700';
                            }

                            return (
                              <tr
                                key={
                                  index
                                }
                                className="hover:bg-slate-50"
                              >

                                <td className="px-5 py-3 font-mono font-bold text-gray-800">
                                  {
                                    item.code
                                  }
                                </td>

                                <td className="px-5 py-3 font-bold text-slate-800">
                                  <span className="bg-purple-100 text-purple-800 px-2.5 py-1 rounded-md">
                                    {
                                      item.percentage
                                    }
                                    PKR
                                  </span>
                                </td>

                                <td className="px-5 py-3 text-center">
                                  <span
                                    className={`text-xs px-3 py-1 rounded-full font-bold ${statusClasses}`}
                                  >
                                    {
                                      statusText
                                    }
                                  </span>
                                </td>

                                <td className="px-5 py-3 text-gray-500 text-xs">
                                  {item.createdAt
                                    ? new Date(
                                        item.createdAt
                                      ).toLocaleString()
                                    : '-'}
                                </td>

                              </tr>
                            );
                          }
                        )
                      )}

                    </tbody>
                  </table>
                </div>
              </div>

              {/* CATEGORY DETAIL */}

              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-6">

                <div className="px-6 py-4 border-b border-gray-100 bg-indigo-900 text-white flex flex-wrap justify-between items-center gap-3">

                  <h2 className="font-bold flex items-center gap-2 text-lg">
                    <Layers
                      size={20}
                      className="text-indigo-200"
                    />
                    Category Detail
                  </h2>

                  <div className="relative w-64 text-gray-800">

                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={16}
                    />

                    <input
                      type="text"
                      placeholder="Search subcategory, label or status..."
                      className="pl-9 pr-4 py-1.5 border rounded-lg w-full text-sm"
                      value={
                        categorySearch
                      }
                      onChange={(event) =>
                        setCategorySearch(
                          event.target.value
                        )
                      }
                    />

                  </div>
                </div>

                <div className="overflow-x-auto">

                  <table className="w-full text-sm">

                    <thead className="bg-slate-100 text-gray-700 text-xs font-bold uppercase tracking-wider">

                      <tr>

                        <th className="px-5 py-3.5 text-left">
                          Gender
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          Subcategory
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          Label
                        </th>

                        <th className="px-5 py-3.5 text-center">
                          Win Weight
                        </th>

                        <th className="px-5 py-3.5 text-center">
                          Status
                        </th>

                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">

                      {filteredCategories.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            className="text-center py-10 text-gray-400"
                          >
                            No category metrics available.
                          </td>
                        </tr>
                      ) : (
                        filteredCategories.map(
                          (
                            category,
                            index
                          ) => {
                            const statusText =
                              String(
                                category.status ||
                                  (category.isActive ===
                                  true
                                    ? 'Active'
                                    : 'Inactive')
                              );

                            const label =
                              category.label ||
                              category.lineItemName ||
                              category.name ||
                              '-';

                            return (
                              <tr
                                key={
                                  index
                                }
                                className="hover:bg-slate-50"
                              >

                                <td className="px-5 py-3">
                                  {
                                    category.gender ||
                                    'male'
                                  }
                                </td>

                                <td className="px-5 py-3 text-gray-600">
                                  {
                                    category.subcategory ||
                                    'Unknown'
                                  }
                                </td>

                                <td className="px-5 py-3 font-semibold text-gray-700">
                                  {label}
                                </td>

                                <td className="px-5 py-3 text-center font-mono font-bold">
                                  {
                                    category.winWeight ??
                                    category.winweight ??
                                    '-'
                                  }
                                </td>

                                <td className="px-5 py-3 text-center">
                                  {statusText}
                                </td>

                              </tr>
                            );
                          }
                        )
                      )}

                    </tbody>
                  </table>
                </div>
              </div>

              {/* STORE DETAILS */}

              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-6">

                <div className="px-6 py-4 border-b border-gray-100 bg-indigo-900 text-white flex flex-wrap justify-between items-center gap-3">

                  <div>

                    <h2 className="font-bold flex items-center gap-2 text-lg">
                      <Store
                        size={20}
                        className="text-indigo-200"
                      />
                      Store-wise Details
                    </h2>

                    <p className="text-xs text-indigo-200 mt-1">
                      Click a store or winner count to view won subcategories
                    </p>

                  </div>

                  <div className="relative w-64 text-gray-800">

                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={16}
                    />

                    <input
                      type="text"
                      placeholder="Search store, city or region..."
                      className="pl-9 pr-4 py-1.5 border rounded-lg w-full text-sm"
                      value={
                        searchTerm
                      }
                      onChange={(event) =>
                        setSearchTerm(
                          event.target.value
                        )
                      }
                    />

                  </div>
                </div>

                <div className="overflow-x-auto">

                  <table className="w-full text-sm">

                    <thead className="bg-slate-100 text-gray-700 text-xs font-bold uppercase tracking-wider">

                      <tr>

                        <th className="px-5 py-3.5 text-left">
                          Store Name
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          City
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          Region
                        </th>

                        <th className="px-5 py-3.5 text-center">
                          Win Rate
                        </th>

                        <th className="px-5 py-3.5 text-center">
                          Winners
                        </th>

                        <th className="px-5 py-3.5 text-center">
                          Won Subcategories
                        </th>

                        <th className="px-5 py-3.5 text-left">
                          Last Activity
                        </th>

                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">

                      {filteredStores.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center py-12 text-gray-400"
                          >
                            No stores match your filters or search
                          </td>
                        </tr>
                      ) : (
                        filteredStores.map(
                          (
                            store
                          ) => {
                            const winRate =
                              store.spins_used >
                              0
                                ? (
                                    ((store.winners_count ||
                                      0) /
                                      store.spins_used) *
                                    100
                                  ).toFixed(
                                    1
                                  )
                                : '0.0';

                            const subcategories =
                              getStoreSubcategories(
                                store
                              );

                            return (
                              <tr
                                key={
                                  store.id
                                }
                                className="hover:bg-indigo-50/30"
                              >

                                <td className="px-5 py-3">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      openStoreWinnerModal(
                                        store
                                      )
                                    }
                                    className="font-medium text-gray-800 hover:text-indigo-700"
                                  >
                                    <span className="inline-flex items-center gap-1.5">
                                      {
                                        store.storeName ||
                                        '-'
                                      }

                                      <Eye
                                        size={
                                          13
                                        }
                                        className="text-indigo-400"
                                      />
                                    </span>
                                  </button>
                                </td>

                                <td className="px-5 py-3 text-gray-600">
                                  {
                                    store.city ||
                                    '-'
                                  }
                                </td>

                                <td className="px-5 py-3">
                                  <span className="bg-indigo-50 text-indigo-700 px-2.5 py-0.5 rounded-full text-xs font-semibold">
                                    {
                                      store.region ||
                                      '-'
                                    }
                                  </span>
                                </td>

                                <td className="px-5 py-3 text-center">
                                  {winRate}%
                                </td>

                                <td className="px-5 py-3 text-center">

                                  <button
                                    type="button"
                                    onClick={() =>
                                      openStoreWinnerModal(
                                        store
                                      )
                                    }
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold"
                                  >
                                    <Trophy
                                      size={
                                        14
                                      }
                                    />

                                    {
                                      store.winners_count ||
                                      0
                                    }

                                    <Eye
                                      size={
                                        13
                                      }
                                    />

                                  </button>

                                </td>

                                <td className="px-5 py-3 text-center">

                                  {subcategories.length >
                                  0 ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openStoreWinnerModal(
                                          store
                                        )
                                      }
                                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-50 text-purple-700 font-semibold"
                                    >
                                      <PackageOpen
                                        size={
                                          14
                                        }
                                      />

                                      {
                                        subcategories.length
                                      }{' '}
                                      Types
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openStoreWinnerModal(
                                          store
                                        )
                                      }
                                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 text-slate-600"
                                    >
                                      <Eye
                                        size={
                                          14
                                        }
                                      />

                                      View Wins
                                    </button>
                                  )}

                                </td>

                                <td className="px-5 py-3 text-gray-500 text-xs">
                                  {store.last_activity
                                    ? new Date(
                                        store.last_activity
                                      ).toLocaleDateString()
                                    : '-'}
                                </td>

                              </tr>
                            );
                          }
                        )
                      )}

                    </tbody>
                  </table>
                </div>
              </div>

              {/* ==================================================
                  COMPLETE WINNERS DETAIL
              ================================================== */}

              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-12">

                <div className="px-6 py-4 border-b border-gray-100 bg-indigo-900 text-white flex flex-wrap justify-between items-center gap-3">

                  <h2 className="font-bold flex items-center gap-2 text-lg">

                    <Trophy
                      size={20}
                      className="text-indigo-200"
                    />

                    Complete Winners Detail

                    <span className="bg-indigo-800 text-indigo-200 text-xs px-2.5 py-0.5 rounded-full ml-1 font-bold border border-indigo-700">
                      {
                        winnersPagination.total
                      }{' '}
                      Records
                    </span>

                  </h2>

                  <div className="flex gap-3 items-center flex-wrap">

                    <div className="relative w-72 text-gray-800">

                      <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={16}
                      />

                      <input
                        type="text"
                        placeholder="Search name, phone, prize, code..."
                        className="pl-9 pr-4 py-1.5 border rounded-lg w-full text-sm"
                        value={
                          winnersSearch
                        }
                        onChange={(event) =>
                          setWinnersSearch(
                            event.target.value
                          )
                        }
                      />

                    </div>

                    <button
                      onClick={
                        exportWinnersCSV
                      }
                      className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1"
                    >
                      <Download
                        size={14}
                      />
                      Export All
                    </button>

                  </div>
                </div>

                {winnersError && (
                  <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertCircle
                      size={18}
                    />
                    {
                      winnersError
                    }
                  </div>
                )}

                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">

                  <table className="w-full min-w-[1250px] text-sm">

                    <thead className="bg-slate-100 text-gray-700 text-xs font-bold uppercase tracking-wider sticky top-0 z-10">

                      <tr>

                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          Region
                        </th>

                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          City
                        </th>

                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          Store
                        </th>

                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          Customer Name
                        </th>

                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          Contact Number
                        </th>

                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          Coupon Code
                        </th>

                        <th className="px-4 py-3 text-center whitespace-nowrap">
                          Discount Amount
                        </th>

                        <th className="px-4 py-3 text-center whitespace-nowrap">
                          Redeemed
                        </th>

                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          Spin Time
                        </th>

                        <th className="px-4 py-3 text-center whitespace-nowrap">
                          Status
                        </th>

                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">

                      {winnersLoading ? (
                        <tr>
                          <td
                            colSpan="10"
                            className="text-center py-12 text-gray-400"
                          >
                            <div className="flex justify-center items-center gap-2">
                              <Loader2
                                size={18}
                                className="animate-spin"
                              />
                              Loading winners...
                            </div>
                          </td>
                        </tr>
                      ) : winners.length ===
                        0 ? (
                        <tr>
                          <td
                            colSpan="10"
                            className="text-center py-12 text-gray-400"
                          >
                            No winner records found
                          </td>
                        </tr>
                      ) : (
                        winners.map(
                          (
                            winner,
                            index
                          ) => {
                            const prizeText =
                              String(
                                winner.prize ||
                                  '-'
                              ).toLowerCase();

                            const prizeClasses =
                              prizeText.includes(
                                '5000'
                              )
                                ? 'bg-green-100 text-green-700'
                                : prizeText.includes(
                                    '2000'
                                  )
                                ? 'bg-blue-100 text-blue-700'
                                : prizeText.includes(
                                    '1000'
                                  )
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-purple-100 text-purple-700';

                            const status =
                              String(
                                winner.status ||
                                  ''
                              ).toLowerCase();

                            return (
                              <tr
                                key={
                                  winner.id ||
                                  index
                                }
                                className="hover:bg-slate-50 transition"
                              >

                                <td className="px-4 py-3 text-left whitespace-nowrap">
                                  <span className="bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full text-xs font-semibold">
                                    {
                                      winner.region
                                    }
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-left text-gray-600 whitespace-nowrap">
                                  {
                                    winner.city
                                  }
                                </td>

                                <td className="px-4 py-3 text-left font-semibold text-gray-800 whitespace-nowrap">
                                  {
                                    winner.storeName
                                  }
                                </td>

                                <td className="px-4 py-3 text-left font-medium text-gray-800 whitespace-nowrap">
                                  {
                                    winner.customerName
                                  }
                                </td>

                                <td className="px-4 py-3 text-left font-mono text-xs text-gray-600 whitespace-nowrap">
                                  {
                                    winner.phone
                                  }
                                </td>

                                <td className="px-4 py-3 text-left whitespace-nowrap">
                                  <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-md font-mono text-xs font-bold">
                                    {
                                      winner.code
                                    }
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-center whitespace-nowrap">
                                  <span
                                    className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${prizeClasses}`}
                                  >
                                    {
                                      winner.prize
                                    }
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-center whitespace-nowrap">
                                  <span
                                    className={`text-xs px-2.5 py-0.5 rounded-full font-bold ${
                                      String(
                                        winner.isRedeemed
                                      ).toLowerCase() ===
                                      'yes'
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-amber-100 text-amber-700'
                                    }`}
                                  >
                                    {
                                      winner.isRedeemed
                                    }
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-left text-gray-500 text-xs whitespace-nowrap">
                                  {winner.createdAt
                                    ? new Date(
                                        winner.createdAt
                                      ).toLocaleString()
                                    : '-'}
                                </td>

                                <td className="px-4 py-3 text-center whitespace-nowrap">
                                  <span
                                    className={`text-xs px-2.5 py-0.5 rounded-full font-bold ${
                                      status ===
                                        'claimed' ||
                                      status ===
                                        'redeemed' ||
                                      status ===
                                        'successfully redeemed'
                                        ? 'bg-green-100 text-green-700'
                                        : status ===
                                            'failed'
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-amber-100 text-amber-700'
                                    }`}
                                  >
                                    {
                                      winner.status
                                    }
                                  </span>
                                </td>

                              </tr>
                            );
                          }
                        )
                      )}

                    </tbody>
                  </table>
                </div>

                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-col md:flex-row items-center justify-between gap-3">

                  <p className="text-sm text-gray-600">

                    Page{' '}

                    <span className="font-semibold">
                      {
                        winnersPagination.page
                      }
                    </span>{' '}

                    of{' '}

                    <span className="font-semibold">
                      {
                        winnersPagination.totalPages
                      }
                    </span>{' '}

                    — Total{' '}

                    <span className="font-semibold">
                      {
                        winnersPagination.total
                      }
                    </span>{' '}

                    records

                  </p>

                  <div className="flex items-center gap-2 flex-wrap">

                    <button
                      onClick={() =>
                        goToPage(1)
                      }
                      disabled={
                        winnersPagination.page <=
                        1
                      }
                      className="px-3 py-1.5 rounded-lg border bg-white text-sm disabled:opacity-50 flex items-center gap-1"
                    >
                      <ChevronsLeft
                        size={16}
                      />
                      First
                    </button>

                    <button
                      onClick={() =>
                        goToPage(
                          winnersPagination.page -
                            1
                        )
                      }
                      disabled={
                        winnersPagination.page <=
                        1
                      }
                      className="px-3 py-1.5 rounded-lg border bg-white text-sm disabled:opacity-50 flex items-center gap-1"
                    >
                      <ChevronLeft
                        size={16}
                      />
                      Prev
                    </button>

                    {pageItems.map(
                      (
                        item,
                        index
                      ) =>
                        item ===
                        '...' ? (
                          <span
                            key={`dots-${index}`}
                            className="px-2 text-gray-400"
                          >
                            <MoreHorizontal
                              size={16}
                            />
                          </span>
                        ) : (
                          <button
                            key={
                              item
                            }
                            onClick={() =>
                              goToPage(
                                item
                              )
                            }
                            className={`px-3 py-1.5 rounded-lg border text-sm ${
                              item ===
                              winnersPagination.page
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white text-gray-700'
                            }`}
                          >
                            {
                              item
                            }
                          </button>
                        )
                    )}

                    <button
                      onClick={() =>
                        goToPage(
                          winnersPagination.page +
                            1
                        )
                      }
                      disabled={
                        winnersPagination.page >=
                        winnersPagination.totalPages
                      }
                      className="px-3 py-1.5 rounded-lg border bg-white text-sm disabled:opacity-50 flex items-center gap-1"
                    >
                      Next
                      <ChevronRight
                        size={16}
                      />
                    </button>

                    <button
                      onClick={() =>
                        goToPage(
                          winnersPagination.totalPages
                        )
                      }
                      disabled={
                        winnersPagination.page >=
                        winnersPagination.totalPages
                      }
                      className="px-3 py-1.5 rounded-lg border bg-white text-sm disabled:opacity-50 flex items-center gap-1"
                    >
                      Last
                      <ChevronsRight
                        size={16}
                      />
                    </button>

                  </div>
                </div>
              </div>

            </>
          )}
        </div>
      </div>

      {/* STORE WINNER MODAL */}

      {storeWinnerModalOpen &&
        selectedStore && (
          <div
            className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={
              closeStoreWinnerModal
            }
          >

            <div
              className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
              onClick={(event) =>
                event.stopPropagation()
              }
            >

              <div className="bg-indigo-900 text-white px-6 py-5 flex items-start justify-between gap-4">

                <div>

                  <div className="flex items-center gap-2">

                    <Store
                      size={20}
                      className="text-indigo-200"
                    />

                    <h3 className="text-xl font-bold">
                      {
                        selectedStore.storeName ||
                        'Store'
                      }
                    </h3>

                  </div>

                  <p className="text-indigo-200 text-sm mt-1">
                    {
                      selectedStore.city ||
                      '-'
                    }{' '}
                    •{' '}
                    {
                      selectedStore.region ||
                      '-'
                    }
                  </p>

                </div>

                <button
                  type="button"
                  onClick={
                    closeStoreWinnerModal
                  }
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                >
                  <X size={20} />
                </button>

              </div>

              <div className="p-6">

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">

                  <div className="bg-indigo-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500">
                      Total Winners
                    </p>

                    <p className="text-2xl font-bold text-indigo-700 mt-1">
                      {
                        selectedStoreWinners
                      }
                    </p>
                  </div>

                  <div className="bg-purple-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500">
                      Subcategory Types
                    </p>

                    <p className="text-2xl font-bold text-purple-700 mt-1">
                      {
                        selectedStoreSubcategories.length
                      }
                    </p>
                  </div>

                 

                </div>

                <div className="border border-gray-200 rounded-xl overflow-hidden">

                  <div className="bg-slate-50 px-4 py-3 border-b border-gray-200">
                    <h4 className="font-bold text-gray-700 flex items-center gap-2">
                      <PackageOpen
                        size={17}
                        className="text-purple-600"
                      />
                      Won Subcategories
                    </h4>
                  </div>

                  {selectedStoreSubcategories.length >
                  0 ? (
                    <div className="divide-y divide-gray-100 max-h-[350px] overflow-y-auto">

                      {selectedStoreSubcategories.map(
                        (
                          item,
                          index
                        ) => {
                          const percentage =
                            selectedStoreWinners >
                            0
                              ? Math.round(
                                  (Number(
                                    item.count ||
                                      0
                                  ) /
                                    selectedStoreWinners) *
                                    100
                                )
                              : 0;

                          return (
                            <div
                              key={`${item.name}-${index}`}
                              className="px-4 py-3"
                            >

                              <div className="flex items-center justify-between gap-4">

                                <div className="min-w-0">

                                  <p className="font-semibold text-gray-800 truncate">
                                    {
                                      item.name
                                    }
                                  </p>

                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {
                                      percentage
                                    }%
                                    {' '}
                                    of store wins
                                  </p>

                                </div>

                                <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold">
                                  {
                                    item.count
                                  }{' '}
                                  Wins
                                </span>

                              </div>

                            </div>
                          );
                        }
                      )}

                    </div>
                  ) : (
                    <div className="px-6 py-12 text-center">

                      <PackageOpen
                        size={42}
                        className="mx-auto text-gray-300 mb-3"
                      />

                      <p className="font-semibold text-gray-600">
                        Won subcategory data is not available yet
                      </p>

                    </div>
                  )}

                </div>

                <div className="flex justify-end mt-5">

                  <button
                    type="button"
                    onClick={
                      closeStoreWinnerModal
                    }
                    className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
                  >
                    Close
                  </button>

                </div>

              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default Reports;